import { combineReducers } from 'redux';
import { simpleReducer } from '@mgc/map-client-shared/lib/ReduxTools';
import moment from 'moment';
import actions from '../actions';
import utils from '../utils';

function _updateAttrAtIndex(state, action, attr) {
    const cartIndex = action.payload.cartIndex;
    const attrValue = action.payload[attr];
    const result = state.slice();

    result[cartIndex][attr] = attrValue;

    return result;
}

function _updateItemDataChangedFlag(state, action) {
    const cartIndex = action.payload.cartIndex;
    const result = state.slice();
    result[cartIndex]['itemDataChanged'] = true;
    return result;
}

function _setEndData(action, startDate) {
    const { soldTo, monthCount, weekCount, dailyPricingEnabled, offerEndDate } = action.payload;

    if (utils.isDateRangeSoldTO(soldTo)) {
        const threeMonthEndDate = moment(startDate)
            .add(3, 'M')
            .subtract(1, 'd');
        const contractEndDate = moment(offerEndDate);

        return threeMonthEndDate.isAfter(contractEndDate)
            ? contractEndDate.toISOString()
            : threeMonthEndDate.toISOString();
    }

    if (dailyPricingEnabled) {
        return null;
    }

    if (monthCount && monthCount > 0) {
        return moment(startDate).add(monthCount, 'M').toISOString();
    }

    if (weekCount && weekCount === 1) {
        return moment(startDate).add(weekCount, 'w').toISOString();
    }

    return null;
}

function _startDate(state) {
    return moment().startOf('day').toISOString();
}

function _transFormItems(state, action) {
    const { userCart, selectedOffer, serversBySite } = action.payload;

    // Filter cart to only process items where the offer matches selectedOffer.id
    const filteredCart = Array.isArray(userCart) ? userCart.filter(item => item.offer === selectedOffer.id) : [];

    //const result = state.slice(); // Make a shallow copy of the current state

    if (filteredCart.length === 0) {
        console.log("No matching cart items for the selected offer.");
        return [];
    }


    const items = filteredCart.map(item => {
        const monthCount = item.duration;
        const weekCount = item.weeks;
        //const startDate = item.validFrom; 
        const startDate = item.validFrom < moment().toISOString() ? moment().toISOString() : item.validFrom;
        const endDate = item.validTo;
        const seats = item.seats;
        const saveForLater = item.save_for_later;
        const productMentorId = item.product.mentorId;
        const cartId = item.id;
        const itemDataChanged = cartId ? false : true;

        const productIndex = selectedOffer.pricing.findIndex(
            pricing => pricing.product && pricing.product.mentorId === productMentorId
        );

        if (productIndex === -1) {
            console.log(`No matching product found for mentorId: ${productMentorId}`);
            return null;
        }

        const siteIndex = serversBySite.findIndex(site => site.mentorId === item.site.mentorId);
        if (siteIndex === -1) {
            console.log(`No matching site found for siteMentorId: ${item.site.mentorId}`);
            return null;
        }

        // Find the serverIndex within the matched site
        const serverIndex = serversBySite[siteIndex].servers.findIndex(
            server => server.mentorId === item.server.mentorId
        );
        if (serverIndex === -1) {
            console.log(`No matching server found for mentorId: ${item.server.mentorId}`);
            return null;
        }

        return {
            productIndex,
            seats,
            startDate,
            endDate,
            monthCount,
            weekCount,
            siteIndex,
            serverIndex,
            saveForLater,
            itemDataChanged,
            cartId
        };
    });

    // Filter out null items resulting from unmatched products, sites, or servers
    const validItems = items.filter(item => item !== null);

    if (validItems.length === 0) {
        console.log("No valid items to process after filtering.");
        return null; // Exit early if no valid items remain
    }


    return validItems; // Return the updated result
}

export default combineReducers({
    instructions: simpleReducer('', {
        UPDATE_CART_INSTRUCTIONS: (a) => a.payload.instructions,
        SELECT_OFFER: '',
        CLEAR_CART: '',
    }),

    selectedItemIndex: simpleReducer(-1, {
        SELECT_ITEM_IN_CART: (a) => a.payload.itemIndex,
        SELECT_OFFER: -1,
        CLEAR_CART: -1,
    }),

    items: (state = [], action) => {
        //items: (state = __debugging_data, action) => {
        let result = null;

        switch (action.type) {
            /* regular cart actions */

            case actions.ADD_ITEM_TO_CART: {
                result = state.slice();
                const startDate = _startDate(state); // Get the start date based on the current state           
                result.push({
                    productIndex: action.payload.productIndex,
                    seats: 1,
                    // Check if start date is behind current time & date then update it to current moment | if empty default set to current moment.
                    startDate: startDate,
                    endDate: _setEndData(action, startDate),
                    monthCount: action.payload.monthCount || 0,
                    weekCount: action.payload.weekCount || 0,
                    siteIndex: action.payload.siteIndex || 0,
                    serverIndex: action.payload.serverIndex || 0,
                    saveForLater: false,
                    itemDataChanged: true, // Set itemDataChanged flag as true for regular cart action -Siva Kishore
                    cartId: "",
                });
                return result;
            }
            case actions.REMOVE_ITEM_FROM_CART: {
                const { itemIndex } = action.payload;
                result = state.slice();
                result.splice(itemIndex, 1);
                return result;
            }

            case actions.ADD_INITIAL_ITEMS_TO_CART: {
                return _transFormItems(state, action);
            }

            case actions.UPDATE_ITEM_SEATS:
                result = _updateItemDataChangedFlag(state, action);
                return _updateAttrAtIndex(result, action, 'seats');

            case actions.UPDATE_ITEM_START_DATE:
                result = _updateItemDataChangedFlag(state, action);
                return _updateAttrAtIndex(result, action, 'startDate');

            case actions.UPDATE_ITEM_DATE_RANGE: {
                const { cartIndex, startDate, endDate } = action.payload;
                result = state.slice();
                result[cartIndex] = {
                    ...result[cartIndex],
                    startDate,
                    endDate,
                    itemDataChanged: true,
                };
                return result;
            }

            case actions.UPDATE_ITEM_END_DATE:
                result = _updateItemDataChangedFlag(state, action);
                return _updateAttrAtIndex(result, action, 'endDate');

            case actions.UPDATE_ITEM_MONTH_COUNT:
                result = _updateItemDataChangedFlag(state, action);
                return _updateAttrAtIndex(result, action, 'monthCount');

            case actions.UPDATE_ITEM_WEEK_COUNT:
                result = _updateItemDataChangedFlag(state, action);
                return _updateAttrAtIndex(result, action, 'weekCount');

            case actions.UPDATE_ITEM_SERVER:
                result = _updateItemDataChangedFlag(state, action);
                result = _updateAttrAtIndex(result, action, 'siteIndex');
                return _updateAttrAtIndex(result, action, 'serverIndex');

            case actions.UPDATE_SAVE_FOR_LATER_LIST: {
                const { cartIndex, saveForLater } = action.payload;
                result = state.slice();
                result[cartIndex].saveForLater = saveForLater;
                // Set the itemDataChanged as true to save to DB
                result[cartIndex]['itemDataChanged'] = true;
                return result;
            }

            case actions.ADD_UNSAVED_ITEMS_TO_CART: {
                const unsavedItems = action.payload.unsavedItems;
                result = state.slice();
                unsavedItems.map(item => {
                    result.push({
                        productIndex: item.productIndex,
                        seats: item.seats,
                        // Check if start date is behind current time & date then update it to current moment | if empty default set to current moment.
                        startDate: item.startDate,
                        endDate: item.endDate,
                        monthCount: item.monthCount || 0,
                        weekCount: item.weekCount || 0,
                        siteIndex: item.siteIndex || 0,
                        serverIndex: item.serverIndex || 0,
                        saveForLater: item.saveForLater,
                        itemDataChanged: item.itemDataChanged, // Set itemDataChanged flag as true for regular cart action -Siva Kishore
                        cartId: item.cartId,
                    });
                });
                return result;
            }

            case actions.CLEAR_CART:
            case actions.SELECT_OFFER:
                return [];

            /* pre-populate the cart for showing off during joyride */

            case actions.START_JOYRIDE:
                return [
                    {
                        productIndex: 0,
                        seats: 1,
                        startDate: moment().toISOString(),
                        endDate: moment().add(1, 'M').toISOString(),
                        monthCount: 1,
                        weekCount: 0,
                        siteIndex: 0,
                        serverIndex: 0,
                        saveForLater: false,
                        cartId: "",
                    },
                ];

            case actions.FINISH_JOYRIDE:
            case actions.CANCEL_JOYRIDE:
                return [];

            default:
                return state;
        }
    },
});



// WEBPACK FOOTER //
// ./app/reducers/cart.js